import type {Component, Editor} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

/**
 * hierarchy objects currently set under selection focus
 */
const inFocus = new Set<Component>();

/**
 * Listeners to manage selection control for component hierarchies
 * @param opt The grapesjs config object
 */
export default (opt: PrimaryConfigOptions): void => {
  const editor = opt.editor;
  const canvasBox = document.querySelector('.js-html-editor-canvas-box');

  if (canvasBox) {
    canvasBox.addEventListener('click', (e: Event) => {
      // eslint-disable-next-line no-restricted-globals
      if (e.target && $(e.target as Element).parents('.gjs-rte-toolbar').length > 0) {
        return;
      }

      editor.runCommand(window.PMW.emails.commands.SET_DEFAULT_SELECTION);
    });
  }

  /**
   * initialize/update hierarchy on adding new components
   */
  editor.on('component:mount', (component: Component) => {
    editor.getModel().skip(() => {
      disableFocus(component);

      if (component.get(window.PMW.emails.props.HIERARCHY_SELECTION)) {
        const root = getHierarchyRootForComponent(component);
        const isRootInFocus = inFocus.has(root);

        enableDescendantsSelection(component);
        isRootInFocus && enableFocus(component);
      }
    });
  });

  /**
   * clear previous focused highlights and set new ones
   */
  editor.on('component:selected', (component: Component) => {
    hideComponentBadge(editor, component);

    editor.getModel().skip(() => {
      clearSelectionFocus();
      if (component.get(window.PMW.emails.props.HIERARCHY_SELECTION)) {
        setSelectionFocus(component);
      }
    });
  });

  /**
   * When a component is deselected, allow the badge to be shown again
   */
  editor.on('component:deselected', (component: Component) => {
    showComponentBadge(editor, component);
  });

  /**
   * If a component is hovered on, show the badge. If it is selected or being edited, then hide the badge
   */
  editor.on('component:hover', (component: Component) => {
    const isEditing = editor.getEditing()?.getId() === component?.getId();
    const isSelected = editor.getSelected()?.getId() === component?.getId();

    if (isEditing || isSelected) {
      hideComponentBadge(editor, component);
    } else {
      showComponentBadge(editor, component);
    }
  });

  /**
   * to update existing components being moved in/out of hierarchy
   */
  editor.on('component:drag:end', ({target, parent}: {target: Component; parent: Component}) => {
    editor.getModel().skip(() => {
      const isRoot = target.get(window.PMW.emails.props.HIERARCHY_ROOT);
      const targetHasHierarchy = target.get(window.PMW.emails.props.HIERARCHY_SELECTION);
      const parentHasHierarchy = parent.get(window.PMW.emails.props.HIERARCHY_SELECTION);
      const noHierarchy = !targetHasHierarchy && !parentHasHierarchy;

      if (isRoot || noHierarchy) {
        return;
      }

      if (parentHasHierarchy) {
        enableFocus(target);
        target.set(window.PMW.emails.props.HIERARCHY_SELECTION, true);
      } else {
        disableFocus(target);
        target.set(window.PMW.emails.props.HIERARCHY_SELECTION, false);
      }

      // (re)select to apply styles and trigger hook
      forceReselection(editor, target);
    });
  });

  /**
   * disable single-column selection from sections
   */
  editor.on(`component:update:${window.PMW.emails.props.COLUMNS_PRESET}`, (component: Component) => {
    editor.getModel().skip(() => {
      const selectableColumns = (component.get(window.PMW.emails.props.COLUMNS_PRESET) as number) > 1;

      component.findType(window.PMW.emails.components.table.PMW_CELL)?.forEach((column: Component) => {
        updateSingleColumnHierarchy(column, selectableColumns);
      });
    });
  });
};

/**
 * toggles column's selection and enables/disables children instead
 * @param column The column component of type: postermywall-cell
 * @param selectable Whether the column should be selectable
 */
function updateSingleColumnHierarchy(column: Component, selectable: boolean): void {
  column.set('hoverable', true);

  if (selectable) {
    enableFocus(column);
    enableSelection(column);
    enableDescendantsSelection(column);
  } else {
    disableFocus(column);
  }
}

/**
 * Force reselection of a component
 * @param editor The editor instance
 * @param component The component to reselect
 */
function forceReselection(editor: Editor, component: Component): void {
  if (!component) {
    return;
  }

  editor.selectRemove(component);
  editor.select(component);
}

function clearSelectionFocus(): void {
  inFocus.forEach(disableFocus);
}

/**
 * for given component, enables selecting children and brings parent hierarchies into focus
 * @param component The component to set focus for
 */
function setSelectionFocus(component: Component): void {
  if (!component) {
    return;
  }

  enableChildrenFocus(component);
  if (component.get('hoverable')) {
    enableFocus(component);
  }

  if (component.parent()?.get(window.PMW.emails.props.HIERARCHY_SELECTION)) {
    setSelectionFocus(component.parent() as Component);
  }
}

/**
 * enables selection on first hoverable children
 * @param component The component to enable children focus for
 */
function enableChildrenFocus(component: Component): void {
  component.components()?.forEach((child: Component) => {
    if (!child.get('hoverable')) {
      enableChildrenFocus(child);
    } else {
      enableFocus(child);
    }
  });
}

/**
 * Enable descendants selection for a component
 * @param component The component to enable descendants selection for
 */
function enableDescendantsSelection(component: Component): void {
  component?.removeClass(window.PMW.emails.HIGHLIGHT_HOVER_CLASS);
  component?.components()?.forEach((child: Component) => {
    if (child.get('hoverable')) {
      enableSelection(child);
    }
    enableDescendantsSelection(child);
  });
}

/**
 * Enable selection for a component
 * @param component The component to enable selection for
 */
function enableSelection(component: Component): void {
  component.set('selectable', true);
}

/**
 * Enable focus for a component
 * @param component The component to enable focus for
 */
function enableFocus(component: Component): void {
  inFocus.add(component);
  component.set('highlightable', true);
}

/**
 * Disable focus for a component
 * @param component The component to disable focus for
 */
function disableFocus(component: Component): void {
  inFocus.delete(component);
  component.set('highlightable', false);
}

/**
 * Get the hierarchy root for a component
 * @param component The component to get hierarchy root for
 * @returns The hierarchy root component
 */
function getHierarchyRootForComponent(component: Component): Component {
  const isRoot = component.get(window.PMW.emails.props.HIERARCHY_ROOT);
  const hasHierarchicalParent = component.parent()?.get(window.PMW.emails.props.HIERARCHY_SELECTION);

  return (isRoot || !hasHierarchicalParent) ? component : getHierarchyRootForComponent(component.parent() as Component);
}

/**
 * Hide component badge
 * @param editor The editor instance
 * @param component The component to hide badge for
 */
function hideComponentBadge(editor: Editor, component: Component): void {
  editor.UndoManager.stop();
  getBadgeElement()?.style?.setProperty('display', 'none', 'important');
  component?.set('badgable', false, {silent: true});
  editor.UndoManager.start();
}

/**
 * Show component badge
 * @param editor The editor instance
 * @param component The component to show badge for
 */
function showComponentBadge(editor: Editor, component: Component): void {
  editor.UndoManager.stop();
  component?.set('badgable', true, {silent: true});
  getBadgeElement()?.style?.setProperty('display', 'block');
  editor.UndoManager.start();
}

/**
 * Get the badge element
 * @returns The badge HTML element
 */
function getBadgeElement(): HTMLElement | undefined {
  // eslint-disable-next-line no-restricted-globals
  return $('.gjs-badge')[1] as HTMLElement | undefined;
}